import { useEffect, useRef, useState } from 'react';

import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Text,
  Textarea,
} from '@chakra-ui/react';

import useAcceptReconciliationFinding from 'app/hooks/useAcceptReconciliationFinding';
import useResolveReconciliationFinding from 'app/hooks/useResolveReconciliationFinding';
import useResolveSalaryDiscrepancy from 'app/hooks/useResolveSalaryDiscrepancy';

import { ReconciliationFindingAction } from 'app/types/reconciliations';

export const RESOLUTION_NOTE_MAX_LENGTH = 500;

interface DialogCopy {
  title: string;
  description: string;
  label: string;
  placeholder: string;
}

const SALARY_RESOLVE_COPY: DialogCopy = {
  title: 'Mark as fixed',
  description:
    'The problem will be recorded as fixed by you. Later checks will not raise it again for the same person and pay cycle.',
  label: 'Why is it fixed?',
  placeholder: 'For example, settled with the employer outside the system',
};

const COPY: Record<ReconciliationFindingAction, DialogCopy> = {
  resolve: {
    title: 'Mark as fixed',
    description:
      'The problem will be recorded as fixed by you. If a later reconciliation finds it again, it will be reopened.',
    label: 'Why is it fixed?',
    placeholder: 'For example, the duplicate was removed by a data fix',
  },
  accept: {
    title: 'Accept',
    description:
      'The problem will be recorded as accepted by you. Later reconciliations keep it closed while they find it exactly the same, and reopen it if anything about it changes.',
    label: 'Why is it accepted?',
    placeholder: 'For example, the report was already delivered and the difference was written off',
  },
};

interface ResolveFindingDialogProps {
  findingId?: string;
  action?: ReconciliationFindingAction;
  onClose: () => void;
  onResolved?: (findingId: string) => void;
  kind?: 'deduction' | 'salary';
}

/**
 * Asks an admin why before marking a problem as fixed by hand or accepting it as known. A salary accrual discrepancy
 * can only be marked as fixed.
 */
function ResolveFindingDialog({
  findingId = undefined,
  action = 'resolve',
  onClose,
  onResolved = undefined,
  kind = 'deduction',
}: ResolveFindingDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [note, setNote] = useState('');
  const resolve = useResolveReconciliationFinding();
  const accept = useAcceptReconciliationFinding();
  const resolveSalary = useResolveSalaryDiscrepancy();
  const isSalary = kind === 'salary';
  const deductionMutation = action === 'accept' ? accept : resolve;
  const submit = isSalary ? resolveSalary.mutate : deductionMutation.mutate;
  const isPending = isSalary ? resolveSalary.isPending : deductionMutation.isPending;
  const copy = isSalary ? SALARY_RESOLVE_COPY : COPY[action];
  const trimmedNote = note.trim();

  useEffect(() => {
    setNote('');
  }, [findingId]);

  const handleConfirm = () => {
    if (!findingId || !trimmedNote) {
      return;
    }
    submit(
      { id: findingId, note: trimmedNote },
      {
        onSuccess: () => {
          onResolved?.(findingId);
          onClose();
        },
      }
    );
  };

  return (
    <AlertDialog
      isOpen={Boolean(findingId)}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
      closeOnOverlayClick={!isPending}
      closeOnEsc={!isPending}
    >
      <AlertDialogOverlay>
        <AlertDialogContent data-testid="resolve-finding-dialog">
          <AlertDialogHeader>{copy.title}</AlertDialogHeader>
          <AlertDialogBody>
            <Text mb="16px">{copy.description}</Text>
            <FormControl isRequired>
              <FormLabel>{copy.label}</FormLabel>
              <Textarea
                value={note}
                onChange={(event) => setNote(event.target.value)}
                maxLength={RESOLUTION_NOTE_MAX_LENGTH}
                placeholder={copy.placeholder}
                data-testid="resolution-note-input"
              />
              <FormHelperText>
                {note.length} / {RESOLUTION_NOTE_MAX_LENGTH}
              </FormHelperText>
            </FormControl>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button
              ref={cancelRef}
              variant="outline"
              onClick={onClose}
              isDisabled={isPending}
              data-testid="cancel-resolve-finding-button"
            >
              Cancel
            </Button>
            <Button
              ml="12px"
              onClick={handleConfirm}
              isLoading={isPending}
              isDisabled={!trimmedNote}
              data-testid="confirm-resolve-finding-button"
            >
              {copy.title}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}

export default ResolveFindingDialog;
